/**
 * Common functionalities for the org-data plugin.
 *
 * @packageDocumentation
 */

export interface Option {
  label: string;
  value: string;
}

export interface Chapter {
  id: string;
  name: string;
}

export interface Platform {
  id: string;
  name: string;
  chapters?: Chapter[];
}

export interface Division {
  id: string;
  name: string;
  platforms: Platform[];
}

export interface OrgData {
  divisions: Division[];
}

export const orgData: OrgData = {
  divisions: [
    {
      id: 'pharmaceuticals',
      name: 'Pharmaceuticals',
      platforms: [
        {
          id: 'customer_engagement_ph',
          name: 'Customer Engagement',
        },
        {
          id: 'digital_lead_commercial_ph',
          name: 'Digital Lead Commercial',
        },
        {
          id: 'dtit_dl_research_development_ph',
          name: 'DTIT DL Research & Development',
        },
        {
          id: 'operational_excellence_ph',
          name: 'Operational Excellence',
        },
      ],
    },
    {
      id: 'crop_science',
      name: 'Crop Science',
      platforms: [
        {
          id: 'customer_experience_cs',
          name: 'Customer Experience',
        },
        {
          id: 'product_pipeline_cs',
          name: 'Product Pipeline',
        },
        {
          id: 'global_data_assets_analytics_cs',
          name: 'Global Data Assets & Analytics',
        },
        {
          id: 'na_commercial_dl_cs',
          name: 'NA Commercial DL',
        },
        {
          id: 'digital_lead_ps_cs',
          name: 'Digital Lead PS',
        },
        {
          id: 'digital_lead_rd_cs',
          name: 'Digital Lead R&D',
        },
        {
          id: 'veg_dl_cs',
          name: 'Veg DL',
        },
        {
          id: 'apac_commercial_dl_cs',
          name: 'APAC Commercial DL',
        },
        {
          id: 'emea_commercial_dl_cs',
          name: 'EMEA Commercial DL',
        },
      ],
    },
    {
      id: 'consumer_health',
      name: 'Consumer Health',
      platforms: [
        {
          id: 'mftg_enablement_platform_ch',
          name: 'Manufacturing Enablement Platform',
        },
        {
          id: 'ch_product_experience2_members_ch',
          name: 'Product Experience',
        },
        {
          id: 'ch_data_assets_analytics_ai_ch',
          name: 'Data Assets, Analytics & AI',
        },
        {
          id: 'veeva_platform_ch',
          name: 'Veeva Platform',
        },
        {
          id: 'distinguished_eng_architecture_council_ch',
          name: 'Distinguished Engineering & Architecture Council',
        },
        {
          id: 'ai_process_automation_ch',
          name: 'AI & Process Automation',
        },
        {
          id: 'integrated_planning_logistics_ch',
          name: 'Integrated Planning & Logistics',
        },
        {
          id: 'cs_product_fulfillment_platform_ch',
          name: 'Product Fulfillment Platform',
        },
        {
          id: 'enterprise_data_analytics_ch',
          name: 'Enterprise Data & Analytics',
        },
        {
          id: 'ph_drug_innovation_ch',
          name: 'Drug Innovation',
        },
        {
          id: 'lab_enablement_platform_ch',
          name: 'Lab Enablement Platform',
        },
        {
          id: 'engineering_enablement_platform_ch',
          name: 'Engineering Enablement Platform',
        },
        {
          id: 'ch_consumer_customer_ch',
          name: 'Consumer & Customer',
        },
        {
          id: 'salesforce_ch',
          name: 'Salesforce',
        },
        {
          id: 's4_erp_enablement_platform_ch',
          name: 'S/4 ERP Enablement Platform',
        },
      ],
    },
    {
      id: 'enabling_functions',
      name: 'Enabling Functions',
      platforms: [
        {
          id: 'ch_product_experience_ef',
          name: 'Product Experience',
        },
        {
          id: 'ph_drug_innovation_ef',
          name: 'Drug Innovation',
        },
        {
          id: 'enterprise_planning_shipping_platform_ef',
          name: 'Enterprise Planning & Shipping Platform',
        },
        {
          id: 'distinguished_eng_architecture_council_ef',
          name: 'Distinguished Engineering & Architecture Council',
        },
        {
          id: 'lab_enablement_platform_ef',
          name: 'Lab Enablement Platform',
        },
        {
          id: 'enterprise_data_analytics_ef',
          name: 'Enterprise Data & Analytics',
        },
        {
          id: 'engineering_enablement_platform_ef',
          name: 'Engineering Enablement Platform',
          chapters: [
            {
              id: 'engineering_enablement_platform_ai_chapter',
              name: 'AI Chapter',
                },
            {
              id: 'engineering_enablement_platform_design_chapter',
              name: 'Design Chapter',
                },
            {
              id: 'engineering_enablement_platform_product_chapter',
              name: 'Product Chapter',
                },
          ],
        },
        {
          id: 'integrated_planning_logistics_ef',
          name: 'Integrated Planning & Logistics',
        },
        {
          id: 'veeva_platform_ef',
          name: 'Veeva Platform',
        },
        {
          id: 'fi_it_te_ai_process_automation_ef',
          name: 'AI & Process Automation',
        },
        {
          id: 'cs_product_fulfillment_platform_ef',
          name: 'Product Fulfillment Platform',
        },
        {
          id: 's4_erp_enablement_platform_ef',
          name: 'S/4 ERP Enablement Platform',
        },
        {
          id: 'salesforce_ef',
          name: 'Salesforce',
        },
        {
          id: 'lab_enablement_ef',
          name: 'Lab Enablement',
        },
        {
          id: 'ch_consumer_customer_experience_ef',
          name: 'Consumer & Customer Experience',
        },
        {
          id: 'mftg_enablement_platform_ef',
          name: 'Manufacturing Enablement Platform',
        },
        {
          id: 'enterprise_architecture_ef',
          name: 'Enterprise Architecture',
        },
      ],
    },
  ],
};

export const divisionOptions: Option[] = orgData.divisions.map(division => ({
  label: division.name,
  value: division.id,
}));

export const platformOptions: Record<string, Option[]> =
  orgData.divisions.reduce((acc, division) => {
    acc[division.id] = division.platforms.map(platform => ({
      label: platform.name,
      value: platform.id,
    }));
    return acc;
  }, {} as Record<string, Option[]>);

export const getPlatformsByDivision = (divisionId: string): Platform[] => {
  const division = orgData.divisions.find(d => d.id === divisionId);
  return division?.platforms || [];
};

export const getChaptersByPlatform = (platformId: string): Chapter[] => {
  for (const division of orgData.divisions) {
    const platform = division.platforms.find(p => p.id === platformId);
    if (platform) {
      return platform.chapters || [];
    }
  }
  return [];
};

export const getAllPlatforms = (): Platform[] => {
  return orgData.divisions.flatMap(division => division.platforms);
};

export const getAllChapters = (): Chapter[] => {
  return orgData.divisions.flatMap(division =>
    division.platforms.flatMap(platform => platform.chapters || []),
  );
};

export interface ExternalRadarSource {
  id: string;
  name: string;
  url: string;
}

export const externalRadarSources: ExternalRadarSource[] = [];